/**
 * Is this device able to sit a proctored interview?
 *
 * Phones and tablets are not, and the reason is not performance — it is that
 * the proctoring rules in InterviewPage terminate on the second
 * `visibilitychange`, and on a handheld that event fires for things the
 * candidate never chose: an incoming call, a notification, the screen locking
 * on its idle timer, the OS backgrounding the tab. Two of those across half an
 * hour is the normal path, not an edge case, so a mobile candidate is close to
 * guaranteed a permanent termination for something they did not do.
 *
 * Blocking them at the door is kinder than letting them start and then ending
 * their interview twenty minutes in.
 *
 * The bias here is deliberately one-way. Letting a phone through costs the
 * candidate a wrongful termination they can appeal; wrongly blocking a real
 * laptop leaves them stuck at a door that will never open, with no path
 * forward at all. So we only ever block on POSITIVE evidence of a handheld —
 * never on a weak signal like a coarse pointer or a missing Fullscreen API,
 * both of which touchscreen Windows laptops report too.
 */

/** @returns {'mobile'|'tablet'|'desktop'} */
export function detectDeviceClass() {
    if (typeof navigator === 'undefined') return 'desktop';

    const ua       = navigator.userAgent || '';
    const uaData   = navigator.userAgentData;
    const platform = navigator.platform || '';
    const touch    = navigator.maxTouchPoints || 0;

    // 1. Client Hints — authoritative where the browser offers them (Chromium).
    //    Only trusted in the positive direction: Chrome on Android in
    //    "Desktop site" mode reports mobile:false with a desktop UA, and we
    //    would rather miss that than start second-guessing a real desktop.
    if (uaData && uaData.mobile === true) return 'mobile';

    // 2. iPadOS 13+ Safari deliberately reports itself as desktop macOS —
    //    same UA, same platform, no iPad anywhere in the string. The one
    //    thing it cannot hide is the touchscreen: no Mac reports touch
    //    points, so MacIntel + touch is an iPad and nothing else.
    if (/MacIntel|MacApple/.test(platform) && touch > 1) return 'tablet';

    // 3. UA strings. Phones first — an Android tablet's UA is an Android
    //    phone's minus the "Mobile" token, so order matters here.
    if (/\b(iPhone|iPod|Windows Phone|IEMobile|BlackBerry|BB10|Opera Mini)\b/i.test(ua)) return 'mobile';
    if (/Android/i.test(ua) && /Mobile/i.test(ua)) return 'mobile';
    if (/\b(iPad|Tablet|PlayBook|Silk|Kindle)\b/i.test(ua)) return 'tablet';
    if (/Android/i.test(ua)) return 'tablet';

    return 'desktop';
}

/** Everything that is not a desktop or laptop is turned away. */
export function isInterviewCapableDevice() {
    return detectDeviceClass() === 'desktop';
}
